import React, { ReactNode } from 'react'

import type {
  TDerivedMetricOptionsDto,
  TDerivedMetricPreviewDto,
  TDerivedMetricUnit
} from '@homepal/api-contract'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'

import SelectSearch from 'components_new/molecules/SelectSearch'
import SwitchItem from 'components_new/molecules/SwitchItem'

import ExpressionEditor from 'components_new/organisms/internal/MetricStudio/ExpressionEditor'
import FieldRow from 'components_new/organisms/internal/MetricStudio/FieldRow'
import FormulaBox from 'components_new/organisms/internal/MetricStudio/FormulaBox'
import Preview from 'components_new/organisms/internal/MetricStudio/Preview'
import TextsEditor from 'components_new/organisms/internal/MetricStudio/TextsEditor'
import {
  EMPTY_EXPRESSION,
  findFactTable,
  formatFormula,
  MetricField,
  MetricFormState,
  missingFieldsSentence
} from 'components_new/organisms/internal/MetricStudio/utils'

const NO_UNIT = 'NO_UNIT'

type UnitOptionId = TDerivedMetricUnit | typeof NO_UNIT

interface SectionProps {
  children: ReactNode
  number: number
  title: string
}

/**
 * A numbered section. The form is long enough that it needs to read as an
 * order of work, not as three unrelated boxes.
 */
const Section = (props: SectionProps) => {
  const { children, number, title } = props

  return (
    <Box sx={{ mb: 5 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'success.light',
            borderRadius: '50%',
            display: 'flex',
            height: 28,
            justifyContent: 'center',
            width: 28
          }}
        >
          <Text component="span" variant="subtitle2">
            {number}
          </Text>
        </Box>
        <Text component="h2" variant="h6">
          {title}
        </Text>
      </Stack>
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          px: 3,
          py: 1
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

/** A stored value as it reads when nobody is editing it. */
const ReadValue = (props: { value: string | null }) => {
  const { value } = props

  return (
    <Text color={value ? 'text.primary' : 'text.secondary'} variant="body2">
      {value || '–'}
    </Text>
  )
}

export interface MetricFormProps {
  edit: boolean
  error: string | null
  isEdit: boolean
  missing: MetricField[]
  onChange: (state: MetricFormState) => void
  onSelectPreviewCustomer: (customerId: string) => void
  options: TDerivedMetricOptionsDto
  preview: TDerivedMetricPreviewDto | null
  previewCustomerId: string | null
  previewing: boolean
  slugTaken: boolean
  state: MetricFormState
  warning: string | null
}

const MetricForm = (props: MetricFormProps) => {
  const {
    edit,
    error,
    isEdit,
    missing,
    onChange,
    onSelectPreviewCustomer,
    options,
    preview,
    previewCustomerId,
    previewing,
    slugTaken,
    state,
    warning
  } = props

  const factTable = findFactTable(options.fact_tables, state.fact_table_id)

  const factTableOptions = options.fact_tables.map((option) => ({
    id: option.id,
    title: option.name,
    body: `${option.metrics.length} mätetal`
  }))

  const categoryOptions = options.categories.map((category) => ({
    id: category.value,
    title: category.name
  }))

  const unitOptions: { id: UnitOptionId; title: string }[] = [
    { id: NO_UNIT, title: 'Ingen enhet' },
    ...options.units.map((unit) => ({ id: unit, title: unit }))
  ]

  const subcategoryOptions = options.subcategories.map((item) => ({
    id: item.slug,
    title: item.name
  }))

  const selectedCategory = categoryOptions.find(
    (option) => option.id === state.category
  )

  const selectedSubcategory = subcategoryOptions.find(
    (option) => option.id === state.texts.subcategory_slug
  )

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 8, width: '100%' }}>
      <Text component="h1" gutterBottom={true} variant="h4">
        {isEdit ? state.texts.title.sv || 'Mätetal' : 'Nytt mätetal'}
      </Text>
      <Text color="text.secondary" sx={{ mb: 4 }} variant="body1">
        Ett mätetal här räknas fram ur andra mätetal från samma faktatabell.
        Välj leden, beskriv resultatet och testa mot en kund. Mätetalet blir
        tillgängligt för alla kunder som har skärningarna konfigurerade.
      </Text>

      <Section number={1} title="Beräkning">
        <FieldRow
          actions={
            edit ? (
              <SelectSearch
                closeOnSelect
                disableClearable={true}
                multiple={false}
                onChange={(value) =>
                  onChange({
                    ...state,
                    fact_table_id: value.id,

                    // Operands of the previous fact table do not exist here.
                    expression: EMPTY_EXPRESSION
                  })
                }
                options={factTableOptions}
                placeholder="Välj faktatabell"
                selected={
                  factTableOptions.find(
                    (option) => option.id === state.fact_table_id
                  ) ?? null
                }
                size="small"
                sx={{ width: '100%' }}
              />
            ) : (
              <ReadValue value={factTable?.name ?? null} />
            )
          }
          field="fact_table"
          helperText="Alla led måste komma från samma faktatabell."
          missing={missing.includes('fact_table')}
        />

        {factTable ? (
          <FieldRow
            field="expression"
            helperText="Två led med ett räknesätt emellan. Ett led kan i sin tur vara ett uttryck."
            missing={missing.includes('expression')}
          >
            <Stack direction="column" sx={{ gap: 2 }}>
              {edit ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: '100%', width: 'max-content' }}>
                    <ExpressionEditor
                      // A new fact table brings new operands, so the sides
                      // start over rather than keeping the ones that were
                      // chosen.
                      key={state.fact_table_id ?? 'none'}
                      expression={state.expression}
                      factTable={factTable}
                      onChange={(expression) =>
                        onChange({ ...state, expression })
                      }
                    />
                  </Box>
                </Box>
              ) : null}
              <FormulaBox
                formula={formatFormula(
                  state.texts.title.sv,
                  state.expression,
                  factTable,
                  state.unit
                )}
                note={
                  state.unit === '%'
                    ? 'Kvoten multipliceras med 100 eftersom enheten är procent.'
                    : null
                }
              />
            </Stack>
          </FieldRow>
        ) : null}
      </Section>

      <Section number={2} title="Beskrivning">
        <FieldRow
          actions={
            edit ? (
              <SelectSearch
                closeOnSelect
                disableClearable={true}
                multiple={false}
                onChange={(value) => onChange({ ...state, category: value.id })}
                options={categoryOptions}
                placeholder="Välj kategori"
                selected={selectedCategory ?? null}
                size="small"
                sx={{ width: '100%' }}
              />
            ) : (
              <ReadValue value={selectedCategory?.title ?? null} />
            )
          }
          field="category"
          helperText="Var mätetalet hör hemma i biblioteket."
          missing={missing.includes('category')}
        />
        <FieldRow
          actions={
            edit ? (
              <SelectSearch
                closeOnSelect
                disableClearable={true}
                multiple={false}
                onChange={(value) =>
                  onChange({
                    ...state,
                    texts: { ...state.texts, subcategory_slug: value.id }
                  })
                }
                options={subcategoryOptions}
                placeholder="Välj underkategori"
                selected={selectedSubcategory ?? null}
                size="small"
                sx={{ width: '100%' }}
              />
            ) : (
              <ReadValue value={selectedSubcategory?.title ?? null} />
            )
          }
          field="subcategory"
          helperText="Underrubriken på den publika mätetalssidan."
          missing={missing.includes('subcategory')}
        />
        <FieldRow
          actions={
            edit ? (
              <SelectSearch
                closeOnSelect
                disableClearable={true}
                multiple={false}
                onChange={(value) =>
                  onChange({
                    ...state,
                    unit: value.id === NO_UNIT ? null : value.id
                  })
                }
                options={unitOptions}
                selected={
                  unitOptions.find(
                    (option) => option.id === (state.unit ?? NO_UNIT)
                  ) ?? null
                }
                size="small"
                sx={{ width: '100%' }}
              />
            ) : (
              <ReadValue value={state.unit} />
            )
          }
          helperText="Visas efter värdet. Procent skalar också kvoten."
          title="Enhet"
        />
        <FieldRow
          actions={
            edit ? (
              <SwitchItem
                checked={state.increase_is_positive}
                label="Ökning är positiv"
                onChange={() =>
                  onChange({
                    ...state,
                    increase_is_positive: !state.increase_is_positive
                  })
                }
                size="small"
              />
            ) : (
              <ReadValue
                value={
                  state.increase_is_positive
                    ? 'Ökning är positiv'
                    : 'Ökning är negativ'
                }
              />
            )
          }
          helperText="Styr om en uppgång färgas grön eller röd."
          title="Riktning"
        />

        <TextsEditor
          edit={edit}
          missing={missing}
          onChange={(texts) => onChange({ ...state, texts })}
          slugTaken={slugTaken}
          texts={state.texts}
          writesTexts={options.writes_texts}
        />
      </Section>

      <Section number={3} title="Förhandsgranskning">
        <Stack direction="column" sx={{ gap: 3, py: 2 }}>
          <Preview
            name={state.texts.title.sv}
            onSelectCustomer={onSelectPreviewCustomer}
            preview={preview}
            previewing={previewing}
            selectedCustomerId={previewCustomerId}
            unit={state.unit}
          />

          {edit && missing.length > 0 ? (
            <Alert severity="info">{missingFieldsSentence(missing)}</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          {warning ? <Alert severity="warning">{warning}</Alert> : null}
        </Stack>
      </Section>
    </Box>
  )
}

export default MetricForm
